import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';

/**
 * What a paper must look like before it can be set.
 *
 * Mirrors the backend's `exams.validation.ts` — the server is the authority,
 * these rules only save a round trip. Keep the two in step.
 *
 * Whether the date falls inside the academic year is deliberately left to the
 * server: the session's own dates are not in the browser, and guessing them
 * here would reject a valid date on an institute whose year runs differently.
 */

export const EXAM_TYPES = [
  'UNIT_TEST',
  'MID_TERM',
  'FINAL',
  'PRACTICAL',
  'ORAL',
  'ASSIGNMENT',
  'FORMATIVE',
  'SUMMATIVE',
] as const;

/** Matches the server's ceiling — and catches a total somebody mistyped. */
export const MAX_MARKS_CEILING = 1000;

/** Mirrors the backend's `ExamBalancePlan` enum. */
export const EXAM_BALANCE_PLANS = [
  'PREPARATION_LEAVE',
  'SECOND_EXAM',
  'REGULAR_CLASSES',
  'EARLY_DISPERSAL',
] as const;

/** 24-hour "HH:MM", the shape a bell time already takes across this product. */
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time like 09:30');

/** A whole number typed into a text box, or null when the box is empty. */
const wholeNumber = (max: number, message: string) =>
  z
    .string()
    .trim()
    .transform((value) => (value === '' ? null : Number(value)))
    .refine(
      (value) => value === null || (Number.isInteger(value) && value >= 0 && value <= max),
      message,
    );

export const examSchema = z
  .object({
    classSectionUuid: z.string().uuid('Choose a class'),
    subjectUuid: z.string().uuid('Choose a subject'),

    name: z
      .string()
      .trim()
      .min(2, 'Give the paper a name')
      .max(120, 'That name is too long')
      // "Unit Test I", "Half-Yearly (Set B)".
      .regex(/^[\p{L}\p{N} .,'()&/-]+$/u, "Letters, numbers, spaces and . , ' ( ) & / - only"),

    examType: z.enum(EXAM_TYPES, {
      errorMap: () => ({ message: 'Choose what kind of paper this is' }),
    }),

    maxMarks: wholeNumber(MAX_MARKS_CEILING, `A total between 1 and ${MAX_MARKS_CEILING}`).refine(
      (value) => value !== null && value >= 1,
      'Say what the paper is out of',
    ),

    /** Empty means "use the institute's own pass percentage". */
    passMarks: wholeNumber(MAX_MARKS_CEILING, 'A whole number, or leave it empty'),

    examDate: z
      .string()
      .trim()
      .min(1, 'Pick the exam date')
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-05'),

    /**
     * When the paper runs, and what the rest of that day holds.
     *
     * Optional because the teacher's own "set a paper" form asks for neither —
     * it sets a unit test, not an exam-timetable row. The office's timetable
     * screen always sends all three.
     */
    startTime: clockTime.nullable().optional(),
    endTime: clockTime.nullable().optional(),
    balancePlan: z.enum(EXAM_BALANCE_PLANS).nullable().optional(),
  })
  // The commonest slip on this form, caught before the round trip.
  .refine((value) => value.passMarks === null || value.passMarks <= (value.maxMarks ?? 0), {
    message: 'The pass mark cannot be above the total',
    path: ['passMarks'],
  })
  // Both ends or neither: a paper that starts and never finishes has nothing
  // the exam timetable can print. Mirrors the server's own refinement.
  .refine((value) => ('startTime' in value) === ('endTime' in value), {
    message: 'Give both the start and the end time, or neither',
    path: ['endTime'],
  })
  .refine((value) => !value.startTime || !value.endTime || value.endTime > value.startTime, {
    message: 'A paper cannot end before it starts',
    path: ['endTime'],
  });

/** What the form holds while it is being filled in — every field a string. */
export interface ExamFormValues {
  classSectionUuid: string;
  subjectUuid: string;
  name: string;
  examType: (typeof EXAM_TYPES)[number];
  maxMarks: string;
  passMarks: string;
  examDate: string;
}

/** What the API takes, once parsed. */
export type ExamPayload = z.infer<typeof examSchema>;

/**
 * `raw` keeps the form holding what the inputs hold. Without it the resolver
 * hands the submit handler the *transformed* values — totals already numbers —
 * while the form state stays strings, so the declared `ExamFormValues` would be
 * a lie and the `parse()` that builds the payload would throw on data that is
 * perfectly valid.
 */
export const examFormResolver: Resolver<ExamFormValues> = zodResolver(examSchema, undefined, {
  raw: true,
});

/** A new paper starts as a unit test out of 25 — the commonest by far. */
export function emptyExamForm(
  classSectionUuid = '',
  subjectUuid = '',
  examDate = '',
): ExamFormValues {
  return {
    classSectionUuid,
    subjectUuid,
    name: '',
    examType: 'UNIT_TEST',
    maxMarks: '25',
    passMarks: '',
    examDate,
  };
}
